import React from "react";

import classNames from "../../utils/classnames";

const LABELS: Record<string, string> = {
  hourly: "Hourly",
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
};

interface RecurrenceBadgeProps {
  recurrence?: string | null;
  /** Interval in minutes, used to label "every_x_minutes" recurrences. */
  intervalMinutes?: number | null;
  /** Optional next-fire preview, e.g. "next Tue" (graft from the Pipeline Momentum direction). */
  nextLabel?: string | null;
  className?: string;
}

/**
 * Soft blue "↻ Daily" pill shown on recurring tasks. Mirrors the chosen
 * "Structured Pills" direction (Direction B) and respects dark mode via
 * semantic Tailwind color steps.
 */
export function RecurrenceBadge({ recurrence, intervalMinutes, nextLabel, className }: RecurrenceBadgeProps) {
  if (!recurrence || recurrence === "none") return null;

  const label =
    recurrence === "every_x_minutes" ? `Every ${intervalMinutes ?? 30}m` : LABELS[recurrence];
  if (!label) return null;

  const title = nextLabel ? `Repeats ${label.toLowerCase()} · next ${nextLabel}` : `Repeats ${label.toLowerCase()}`;

  return (
    <span
      className={classNames(
        "inline-flex items-center gap-1 rounded-full border px-1.5 py-px text-[10px] font-medium whitespace-nowrap",
        "border-sky-200 bg-sky-50 text-sky-700",
        "dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-300",
        className,
      )}
      data-test-id="recurrence-badge"
      title={title}
    >
      <span aria-hidden="true" className="text-[11px] leading-none">
        &#8635;
      </span>
      {label}
      {nextLabel ? <span className="text-sky-500 dark:text-sky-400">&middot; {nextLabel}</span> : null}
    </span>
  );
}
